import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?inline";
import frauncesLatin from "../assets/fonts/fraunces-latin.woff2?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteHeader } from "@/components/site/header";
import { SiteFooter } from "@/components/site/footer";
import { site } from "@/data/site";

function NotFoundComponent() {
  return (
    <div className="shell flex min-h-[70vh] flex-col justify-center py-20">
      <p className="eyebrow">Page not found</p>
      <h1 className="display-xl mt-4 max-w-3xl">This page has found another home.</h1>
      <p className="lede mt-6">
        The page you were looking for is not here. You can start from the workshop floor, browse recent
        creations, or tell Paul what you have in mind.
      </p>
      <div className="mt-9 flex flex-wrap gap-3">
        <Link
          to="/"
          className="tap-safe inline-flex items-center justify-center rounded-sm bg-forest px-6 text-sm font-semibold text-primary-foreground"
        >
          Go home
        </Link>
        <Link
          to="/gallery"
          className="tap-safe inline-flex items-center justify-center rounded-sm border border-ink/25 px-6 text-sm font-semibold hover:bg-paper"
        >
          Browse all creations
        </Link>
        <Link
          to="/design-your-piece"
          className="tap-safe inline-flex items-center justify-center rounded-sm border border-ink/25 px-6 text-sm font-semibold hover:bg-paper"
        >
          Design your piece
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="shell flex min-h-[70vh] flex-col justify-center py-20">
      <h1 className="display-lg">This page didn&apos;t load</h1>
      <p className="lede mt-4">
        Something went wrong on our end. You can try again, or email Paul at {site.email}.
      </p>
      <div className="mt-8 flex flex-wrap gap-3">
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="tap-safe inline-flex items-center justify-center rounded-sm bg-forest px-6 text-sm font-semibold text-primary-foreground"
        >
          Try again
        </button>
        <a
          href="/"
          className="tap-safe inline-flex items-center justify-center rounded-sm border border-ink/25 px-6 text-sm font-semibold hover:bg-paper"
        >
          Go home
        </a>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Paul's Pens n Things | Handcrafted Pens & Gifts in Arkansas" },
      {
        name: "description",
        content:
          "One-of-a-kind handcrafted pens, lathe-turned gifts and CNC-carved pieces made around your story by Paul Sherman in Paragould, Arkansas.",
      },
      { name: "author", content: "Paul Sherman" },
      { name: "google-site-verification", content: "H3mrvsOQQ-7lgqsOdCRlZ9OFiCn2t1CnlOppNBl4two" },
      { property: "og:site_name", content: "Paul's Pens n Things" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "theme-color", content: "#F7F3EC" },
      { name: "color-scheme", content: "light" },
      { property: "og:title", content: "Paul's Pens n Things | Handcrafted Pens & Gifts in Arkansas" },
      { name: "twitter:title", content: "Paul's Pens n Things | Handcrafted Pens & Gifts in Arkansas" },
      { property: "og:description", content: "One-of-a-kind handcrafted pens, lathe-turned gifts and CNC-carved pieces made around your story by Paul Sherman in Paragould, Arkansas." },
      { name: "twitter:description", content: "One-of-a-kind handcrafted pens, lathe-turned gifts and CNC-carved pieces made around your story by Paul Sherman in Paragould, Arkansas." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/H11f8sPnVxbVMPidooAs9ItoKYe2/social-images/social-1785419148394-CLAP_P_classic_style_pen_and_pencil_set.webp" },
      { property: "og:image:alt", content: "Classic style handcrafted pen and pencil set by Paul's Pens n Things" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/H11f8sPnVxbVMPidooAs9ItoKYe2/social-images/social-1785419148394-CLAP_P_classic_style_pen_and_pencil_set.webp" },
      { name: "twitter:image:alt", content: "Classic style handcrafted pen and pencil set by Paul's Pens n Things" },
    ],
    links: [
      {
        rel: "preload",
        href: frauncesLatin,
        as: "font",
        type: "font/woff2",
        crossOrigin: "anonymous",
      },
    ],
    styles: [{ children: appCss }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: site.name,
          description:
            "Handcrafted pens, lathe-turned gifts and CNC-carved personalized pieces, made to order in Paragould, Arkansas.",
          email: site.email,
          telephone: site.phoneHref,
          url: site.domain,
          address: {
            "@type": "PostalAddress",
            addressLocality: site.city,
            addressRegion: "AR",
            addressCountry: "US",
          },
          founder: { "@type": "Person", name: site.maker },
          foundingDate: "2006",
          knowsAbout: [
            "Handcrafted pens",
            "Wood turning",
            "CNC carving",
            "Personalized gifts",
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-sm focus:bg-ink focus:px-4 focus:py-3 focus:text-sm focus:font-semibold focus:text-canvas"
      >
        Skip to content
      </a>
      <SiteHeader />
      <main id="main" className="relative z-10">
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
      </main>
      <SiteFooter />
    </>
  );
}
